import type {
  AnalyticsEvent,
  AnalyticsImplementation,
} from '@backstage/frontend-plugin-api';

type Properties = Record<string, string | number | boolean>;

/** The slice of the App Insights SDK this module calls. */
export interface TelemetryClient {
  trackPageView(pageView: {
    name?: string;
    uri?: string;
    properties?: Properties;
  }): void;
  trackEvent(event: { name: string }, properties?: Properties): void;
}

export type TelemetryClientLoader = (
  connectionString: string,
) => Promise<TelemetryClient>;

/**
 * The SDK is a separate chunk, loaded after the first event rather than with
 * the app. Cookies are off: the portal has no consent banner, so users and
 * sessions are counted per page load instead of across visits. The SDK also
 * records unhandled exceptions and the app's fetch calls, with W3C trace
 * headers the backend's OpenTelemetry joins to its own requests.
 */
export const loadApplicationInsights: TelemetryClientLoader =
  async connectionString => {
    const { ApplicationInsights } = await import(
      '@microsoft/applicationinsights-web'
    );
    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        disableCookiesUsage: true,
        enableAutoRouteTracking: false,
        enableCorsCorrelation: false,
      },
    });
    appInsights.loadAppInsights();
    return appInsights;
  };

function toProperties(event: AnalyticsEvent): Properties {
  const properties: Properties = { subject: event.subject };
  for (const [key, value] of Object.entries(event.context)) {
    if (value !== undefined) properties[key] = value;
  }
  if (event.value !== undefined) properties.value = event.value;
  return { ...properties, ...event.attributes };
}

/**
 * Sends Backstage analytics events to Application Insights: `navigate`
 * becomes a page view, every other action a custom event named after it.
 * Without a connection string (local dev, tests) events are dropped and the
 * SDK is never loaded.
 */
export function createApplicationInsightsAnalytics(
  connectionString: string | undefined,
  load: TelemetryClientLoader = loadApplicationInsights,
): AnalyticsImplementation {
  if (!connectionString) return { captureEvent: () => {} };

  let client: Promise<TelemetryClient | undefined> | undefined;
  const getClient = () => {
    // A failed load (blocked by an ad blocker, say) must never break the page.
    client ??= load(connectionString).catch(() => undefined);
    return client;
  };

  return {
    captureEvent(event) {
      getClient().then(appInsights => {
        if (!appInsights) return;
        if (event.action === 'navigate') {
          appInsights.trackPageView({
            name: document.title,
            uri: event.subject,
            properties: toProperties(event),
          });
        } else {
          appInsights.trackEvent({ name: event.action }, toProperties(event));
        }
      });
    },
  };
}
